import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Grid,
  Card,
  Typography,
  Avatar,
  Chip,
  Divider,
  Paper,
} from "@mui/material";

import { FaRegBuilding } from "react-icons/fa";
import { IoNewspaperOutline, IoPeopleOutline } from "react-icons/io5";
import { RiUserSearchLine, RiWebcamLine } from "react-icons/ri";
import { LiaFileInvoiceSolid } from "react-icons/lia";
import { MdVerified } from "react-icons/md";
import { RxCrossCircled } from "react-icons/rx";

import { toast, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { VendorProfile } from "../../../Redux/action/VendorProfileAction";
import { InterviewPlannedAction } from "../../../Redux/action/InterviewPlannedAction";
import { TeamTableAction } from "../../../Redux/action/TeamTableAction";

import dummyDp from "../../../images/dummy_dp.png";
import ContactInfo from "./ContactInfo";
import InterviewList from "./InterviewList";
import LinearProgress from "../../Candidate/CandidateDashboard/LinearProgress";
import { JobCountStats } from "../../../Services/vendorServices";
import axios from "axios";

import "./vendordash.scss";
import { formatLastLogin } from "../../../utils/dateFormatter";

export async function getPercentage(vendorId) {
  try {
    const result = await axios.get(
      `${process.env.REACT_APP_API_PATH}/ebench/get_profile_completion_percentage?vendorId=${vendorId}`
    );
    const data = await result?.data;
    return data;
  } catch (err) {
    console.log("err", err);
  }
}

const VendorDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const userInfo = useSelector((state) => state?.VendorProfile?.result);
  const interviewList = useSelector((state) => state?.InterviewPlanned?.result);
  const { userResult } = useSelector((state) => state.Auth);

  const [showOverlay, setShowOverlay] = useState(false);
  const [image, setImage] = useState({ preview: "", raw: "" });
  const [dataCount, setDataCount] = useState({});
  const [percentage, setPercentage] = useState(0);
  const [jobCount, setJobCount] = useState();

  useEffect(() => {
    if (!userResult?.vendorid) return;
    dispatch(TeamTableAction(userResult?.vendorid));
    dispatch(VendorProfile(userResult?.vendorid));
    dispatch(TeamTableAction(userResult?.vendorid));
  }, [dispatch, userResult]);

  useEffect(() => {
    setImage(userInfo?.vendorProfileImageUrl);
  }, [userInfo]);

  useEffect(() => {
    if (!userResult?.vendorid) return;
    dispatch(InterviewPlannedAction(userResult?.vendorid, 0, 2));
  }, [dispatch, userResult?.vendorid]);

  const handleFileInputChange = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getAllDataCount = useCallback(async () => {
    try {
      const result = await axios.get(
        `${process.env.REACT_APP_API_PATH}/ebench/get_vendor_dashboard_stats?vendorId=${userResult?.vendorid}`
      );
      const data = result.data;
      if (data?.data) {
        setDataCount(data.data);
      }
    } catch (err) {
      console.log("err", err);
    }
  }, [userResult?.vendorid]);

  useEffect(() => {
    if (!userResult?.vendorid) return;

    getPercentage(userResult?.vendorid)
      .then((res) => setPercentage(res?.data))
      .catch((err) => console.log("err", err));

    getAllDataCount();

    JobCountStats(userResult?.vendorid)
      .then((res) => setJobCount(res))
      .catch((err) => console.log("err", err));
  }, [userResult?.vendorid, getAllDataCount]);

  function handleVerification(item) {
    if (!userInfo?.isAccountVerified) {
      toast.warn(
        userInfo?.msadocUrl
          ? "Verification pending from admin"
          : "Please complete your profile",
        {
          toastId: "worningError",
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          transition: Zoom,
        }
      );
      return false;
    }
    navigate(`${item}`);
  }

  if (!userInfo || !userResult) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            border: "6px solid #eee",
            borderTop: "6px solid #286a94",
            borderRadius: "50%",
            width: 60,
            height: 60,
            animation: "spin 1s linear infinite",
          }}
        />
        <Typography sx={{ fontSize: 16, color: "#555" }}>
          Loading data...
        </Typography>

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </Box>
    );
  }

  const accentColor = "#286a94";
  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f3f4f8",
          p: { xs: 2, md: 3 },
        }}
      >
        <Grid container spacing={3}>
          {/* LEFT COLUMN – Profile & Contact */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                position: "sticky",
                top: 16,
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              {/* Profile Card */}
              <Card
                sx={{
                  borderRadius: 4,
                  p: 3,
                  border: "1px solid rgba(148,163,184,0.25)",
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(248,250,252,0.98))",
                  boxShadow:
                    "0 18px 40px rgba(15,23,42,0.06), 0 0 0 1px rgba(255,255,255,0.6) inset",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 1.5,
                  }}
                >
                  {/* Avatar with upload overlay */}
                  <Box
                    sx={{
                      position: "relative",
                      width: 120,
                      height: 120,
                    }}
                    onMouseEnter={() => setShowOverlay(true)}
                    onMouseLeave={() => setShowOverlay(false)}
                    // onClick={handleFileInputChange}
                  >
                    <Avatar
                      src={
                        userInfo?.vendorProfileImageUrl
                          ? userInfo?.vendorProfileImageUrl
                          : dummyDp
                      }
                      alt="avatar"
                      sx={{
                        width: 120,
                        height: 120,
                        border: "3px solid rgba(40,106,148,0.5)",
                        boxShadow:
                          "0 15px 30px rgba(15,23,42,0.25), 0 0 0 4px rgba(255,255,255,0.95)",
                        cursor: "default",
                      }}
                      imgProps={{
                        loading: "eager",
                        decoding: "async",
                      }}
                    />
                    {/* {showOverlay && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(15,23,42,0.55)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                        }}
                      >
                        <IoCloudUploadOutline size={28} color="#fff" />
                        <Typography
                          variant="caption"
                          sx={{ color: "#fff", mt: 0.5 }}
                        >
                          Upload Image
                        </Typography>
                      </Box>
                    )} */}
                  </Box>

                  {/* Hidden file input */}
                  {/* <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                  /> */}

                  {/* Company Name + Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <FaRegBuilding color={accentColor} />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#0f172a" }}
                    >
                      {userInfo?.company ? userInfo?.company : "N/A"}
                    </Typography>
                  </Box>

                  {/* Last Login */}
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", mt: 0.5 }}
                  >
                    <span style={{ color: accentColor, fontWeight: 600 }}>
                      Last Login:&nbsp;
                    </span>
                    {formatLastLogin(userInfo?.lastLogin)}
                  </Typography>

                  {/* Verification Chip */}
                  <Box sx={{ mt: 0.5 }}>
                    {userInfo?.isAccountVerified === true ? (
                      <Chip
                        size="small"
                        sx={{
                          borderRadius: 999,
                          bgcolor: accentColor,
                          color: "#fff",
                          px: 1,
                        }}
                        icon={
                          <MdVerified
                            size={18}
                            style={{ color: "#fff", marginLeft: 4 }}
                          />
                        }
                        label="Verified"
                      />
                    ) : userInfo?.isAccountVerified === false ? (
                      <Chip
                        size="small"
                        sx={{
                          borderRadius: 999,
                          bgcolor: "#dc3545",
                          color: "#fff",
                          px: 1,
                        }}
                        icon={
                          <RxCrossCircled
                            size={18}
                            style={{ color: "#fff", marginLeft: 4 }}
                          />
                        }
                        label="Unverified"
                      />
                    ) : null}
                  </Box>

                  {/* Vendor ID */}
                  {/* <Typography variant="body2" sx={{ color: "#4b5563", mt: 1 }}>
                    <b>VENDOR ID : </b>
                    {userInfo?.vendorEmpId}
                  </Typography> */}

                  {/* Profile Progress */}
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: "80%",
                      mt: 2,
                      mx: "auto", // <-- centers horizontally
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <LinearProgress profileProgress={percentage} />
                  </Box>
                </Box>

                {/* Description */}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mt: 2.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "#286a94",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    About Company
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      lineHeight: 1.55,
                      textAlign: "justify",
                    }}
                  >
                    {userInfo?.description || "No description available."}
                  </Typography>
                </Box>
              </Card>

              {/* Contact Info Card */}
              <ContactInfo userInfo={userInfo} />
            </Box>
          </Grid>

          {/* RIGHT COLUMN – Dashboard & Interviews */}
          <Grid item xs={12} md={8}>
            {/* Header name + verification */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 4,
                px: 3,
                py: 2.5,
                border: "1px solid rgba(148,163,184,0.25)",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.99), rgba(241,245,249,0.98))",
                boxShadow:
                  "0 16px 36px rgba(15,23,42,0.08), 0 0 0 1px rgba(255,255,255,0.6) inset",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#0E3F6F",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getGreeting()},{" "}
                    {userInfo?.firstName + " " + userInfo?.lastName || "there"}{" "}
                    ✨
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", mt: 0.5 }}
                  >
                    Welcome back to your vendor dashboard
                  </Typography>
                </Box>

                <Box sx={{ textAlign: { xs: "left", sm: "right" }, mt: 1 }}>
                  <Typography
                    sx={{
                      display: "inline-block",
                      background: "#e8f3fa",
                      color: "#286a94",
                      px: 2,
                      py: 0.6,
                      borderRadius: "10px",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                    }}
                  >
                    VENDOR ID: {userInfo?.vendorEmpId || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* DASHBOARD STATS */}
            <Box
              sx={{
                mb: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: accentColor,
                  letterSpacing: 0.5,
                }}
              >
                DASHBOARD
              </Typography>
              <Grid container spacing={2.2}>
                {[
                  {
                    title: "Team Members",
                    icon: <IoPeopleOutline size={22} color={accentColor} />,
                    stats: [
                      {
                        label: "Active",
                        value: dataCount?.numberOfActiveTeamMember,
                      },
                      {
                        label: "Inactive",
                        value: dataCount?.numberOfInactiveTeamMember,
                      },
                    ],
                    url: "/team-list",
                  },
                  {
                    title: "Interviews",
                    icon: <RiWebcamLine size={22} color={accentColor} />,
                    stats: [
                      {
                        label: "Scheduled",
                        value: dataCount?.numberOfScheduledInterview,
                      },
                      {
                        label: "Completed",
                        value: dataCount?.numberOfCompletedInterview,
                      },
                      {
                        label: "Cancelled",
                        value: dataCount?.numberOfCancelledInterview,
                      },
                    ],
                    url: "/schedule-interview",
                  },
                  {
                    title: "My Jobs",
                    icon: <IoNewspaperOutline size={22} color={accentColor} />,
                    stats: [
                      { label: "Posted", value: dataCount?.numberOfPostedJobs },
                      {
                        label: "On Hold",
                        value: dataCount?.numberOfOnHoldJobs,
                      },
                      {
                        label: "Later",
                        value: dataCount?.numberOfPostLaterJobs,
                      },
                    ],
                    url: "/posted_jobs",
                  },
                  {
                    title: "Search Candidates",
                    icon: <RiUserSearchLine size={22} color={accentColor} />,
                    quickAction:
                      "Browse candidates from our verified talent network",
                    url: "/all-candidate-list",
                  },
                  {
                    title: "Purchase Orders",
                    icon: <LiaFileInvoiceSolid size={22} color={accentColor} />,
                    stats: [
                      {
                        label: "Seller",
                        value: dataCount?.numberOfSupplierPurchaseOrder,
                      },
                      {
                        label: "Buyer",
                        value: dataCount?.numberOfClientPurchaseOrder,
                      },
                    ],
                    url: "/purchase-details",
                  },
                  {
                    title: "Search Jobs",
                    icon: <IoNewspaperOutline size={22} color={accentColor} />,
                    stats: [
                      { label: "New Jobs", value: jobCount?.jobCount },
                      { label: "Applied", value: jobCount?.appliedJobCount },
                    ],
                    url: "/all-new-jobs-list",
                  },
                ].map((item, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Box
                      onClick={() => handleVerification(item.url)}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        cursor: "pointer",
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        minHeight: 145, // 👈 Reduced height
                        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                        transition: "0.2s",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          borderColor: accentColor,
                          boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      {/* Header Row */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "10px",
                            background: "rgba(40,106,148,0.09)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {item.icon}
                        </Box>

                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "15px",
                            color: "#1e293b",
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>

                      {/* Stats / Text */}
                      {item.stats ? (
                        <Box sx={{ mt: 0.5 }}>
                          {item.stats.map((stat, sIndex) => (
                            <Box
                              key={sIndex}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "13px",
                                color: "#6b7280",
                              }}
                            >
                              {stat.label}
                              <Typography
                                sx={{ fontWeight: 600, color: "#111827" }}
                              >
                                {String(stat.value ?? 0).padStart(2, "0")}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography
                          sx={{ fontSize: "13px", mt: 0.5, color: "#64748b" }}
                        >
                          {item.quickAction}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* INTERVIEW LIST */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 3,
                border: "1px solid rgba(148,163,184,0.25)",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.99), rgba(248,250,252,1))",
                boxShadow:
                  "0 16px 36px rgba(15,23,42,0.08), 0 0 0 1px rgba(255,255,255,0.7) inset",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 2, color: "#0f172a" }}
              >
                Interview List
              </Typography>
              <InterviewList interviewList={interviewList} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default VendorDashboard;
